import Game from '../Game';
import { IGameState } from '../State/Game/IGameState';
import { UnlockType } from '../State/Player/IPlayerState';
import {
    getCompletedLevels,
    getCurrentLevelNumber,
} from '../State/Player/PlayerLevelHelper';
import DataManager from './DataManager';
import { MathHelper } from '../Utility/MathHelper';

const LEVEL_PER_MAP = 60;

export default class Player {
    public data: DataManager;

    /**
     * This variable is defined here due to tracking being called during state dispatch.
     * If tracking can be refactored to occur *outside* of reducers (like DDNA) then this can be
     * removed and replaced with player-state FTUE.
     */
    public FTUE: boolean;

    constructor() {
        this.data = new DataManager();
    }

    public getAvatarTexture() {
        if (Game.resource.existsAndLoaded('player_avatar')) {
            return 'player_avatar';
        }

        return 'no_avatar';
    }

    public getTotalStars(): number {
        return Game.state
            .getState()
            .playerState.levels.saga.reduce((a, b) => a + b.stars, 0);
    }

    public getLevelStars(levelNumber: number): number {
        const levelData = Game.state
            .getState()
            .playerState.levels.saga.find(
                level => level.levelNumber === levelNumber
            );
        if (levelData) {
            return levelData.stars;
        } else {
            return 0;
        }
    }

    /**
     * Returns the number of stars acquired in the given world.
     * @param worldNumber
     */
    public getWorldStars(worldNumber: number): number {
        const index = (worldNumber - 1) * LEVEL_PER_MAP;
        const worldData = Game.state
            .getState()
            .playerState.levels.saga.slice(index, index + LEVEL_PER_MAP);
        return worldData.reduce((a, b) => a + b.stars, 0);
    }

    /**
     * If player level is greater than the highest level in the
     * current world return the last level in this world.
     *
     * If the player level is less than the lowest level in the
     * current world return the first level in this world.
     *
     * @param worldNumber
     */
    public getCurrentWorldLevel(worldNumber: number): number {
        return MathHelper.clamp(
            this.getHighestLevelNumber(), // value
            this.getFirstWorldLevel(worldNumber), // min
            this.getLastWorldLevel(worldNumber) // max
        );
    }

    public getFirstWorldLevel(worldNumber: number): number {
        return LEVEL_PER_MAP * (worldNumber - 1) + 1;
    }

    public getLastWorldLevel(worldNumber: number): number {
        return LEVEL_PER_MAP * worldNumber;
    }

    public getHighestLevelNumber(): number {
        return getCurrentLevelNumber(Game.state.getState());
    }

    /**
     * Player's currently active world different than the highest unlocked world if travelling.
     */
    public getActiveWorldNumber(): number {
        return Game.state.getState().playerState.activeWorld;
    }

    /**
     * Returns the number of unlocked chests in the specified world.
     * Currently depends on having exactly 3 chests on each world and their ID is correctly
     * incrementing across world data JSON files.
     * @param world
     */
    public getWorldChestsUnlocked(world: number): number {
        return Game.state
            .getState()
            .playerState.playerUnlocks.filter(
                unlock =>
                    unlock.type === UnlockType.CHEST &&
                    Math.floor((parseInt(unlock.id.split('_')[1]) - 1) / 3) ===
                        world - 1
            ).length;
    }

    public handleStateChanged(state: IGameState): void {
        this.data.handleStateChanged(state);
    }
}
